import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import AssessmentEditor from './AssessmentEditor';

const { FiPlus, FiEdit2, FiTrash2, FiSearch, FiArrowLeft, FiLayers, FiEye, FiCheck, FiX } = FiIcons;

const AdminAssessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);

  const fetchAssessments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('assessments_1773012000000')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (!error) setAssessments(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this assessment? This cannot be undone.')) {
      const { error } = await supabase
        .from('assessments_1773012000000')
        .delete()
        .eq('id', id);
      
      if (!error) fetchAssessments();
    }
  };

  const filtered = assessments.filter(a => 
    a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <Link to="/dashboard" className="text-gray-500 hover:text-blue-600 flex items-center mb-4 font-bold text-sm transition-colors">
              <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Dashboard
            </Link>
            <h1 className="text-4xl font-black text-gray-900 font-ubuntu">Catalog CMS</h1>
            <p className="text-gray-500">Manage the business assessments available to your users.</p>
          </div>
          <button 
            onClick={() => { setEditingAssessment(null); setIsEditorOpen(true); }}
            className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all flex items-center gap-2"
          >
            <SafeIcon icon={FiPlus} /> Create New Assessment
          </button>
        </header>

        {/* Search & Stats */}
        <div className="bg-white p-6 rounded-3xl border border-gray-200 mb-8 flex flex-col md:flex-row gap-6 items-center">
          <div className="flex-1 relative w-full">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search catalog by title or category..."
              className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-4">
            <div className="px-6 py-3 bg-blue-50 rounded-2xl border border-blue-100">
              <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest leading-none mb-1">Total Items</p>
              <p className="text-xl font-black text-blue-600">{assessments.length}</p>
            </div>
            <div className="px-6 py-3 bg-green-50 rounded-2xl border border-green-100">
              <p className="text-[10px] font-bold text-green-400 uppercase tracking-widest leading-none mb-1">Active</p>
              <p className="text-xl font-black text-green-600">{assessments.filter(a => a.is_active).length}</p>
            </div>
          </div>
        </div>

        {/* Catalog Table */}
        <div className="bg-white rounded-[2.5rem] border border-gray-200 shadow-xl shadow-gray-200/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Assessment</th>
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category</th>
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Pricing (AI/Exp)</th>
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan="5" className="p-20 text-center text-gray-400 italic">Loading catalog...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="5" className="p-20 text-center text-gray-400 italic">No assessments found matching your search.</td></tr>
                ) : (
                  filtered.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                            <SafeIcon icon={FiLayers} />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 leading-tight">{a.title}</p>
                            <p className="text-xs text-gray-400 mt-1">{a.duration} • {a.questions} Qs</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-[10px] font-bold uppercase tracking-widest">
                          {a.category}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-bold text-blue-600">${a.price_ai}</span>
                          <span className="text-[10px] font-bold text-purple-400">${a.price_expert}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${a.is_active ? 'text-green-500' : 'text-red-400'}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${a.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                          {a.is_active ? 'Active' : 'Draft'}
                        </div>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <div className="flex justify-end gap-2">
                          <Link to={`/assessment/${a.slug}`} target="_blank" className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                            <SafeIcon icon={FiEye} />
                          </Link>
                          <button 
                            onClick={() => { setEditingAssessment(a); setIsEditorOpen(true); }}
                            className="p-2 text-gray-400 hover:text-purple-600 transition-colors"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button 
                            onClick={() => handleDelete(a.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isEditorOpen && (
          <AssessmentEditor 
            onClose={() => setIsEditorOpen(false)} 
            onSuccess={() => { setIsEditorOpen(false); fetchAssessments(); }}
            initialData={editingAssessment}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAssessments;